import { Component } from 'react';

/**
 * Error Boundary — catches React render errors and shows a fallback UI
 * instead of crashing the entire page.
 * 
 * Usage:
 *   <ErrorBoundary fallback="section">
 *     <YourComponent />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[ErrorBoundary] ${this.props.name || 'Unknown'}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Minimal fallback — doesn't break layout
      if (this.props.fallback === 'silent') return null;
      
      if (this.props.fallback === 'inline') {
        return (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            Something went wrong loading this section.
            <button 
              onClick={() => this.setState({ hasError: false, error: null })}
              className="ml-2 text-red-700 underline font-medium"
            >
              Retry
            </button>
          </div>
        );
      }

      // Default: card-style fallback
      return (
        <div className="p-6 text-center bg-white rounded-xl border border-gray-200" data-testid="error-boundary-fallback">
          <p className="text-sm font-medium text-gray-900 mb-1">Something went wrong</p>
          <p className="text-xs text-gray-500 mb-3">This section encountered an error.</p>
          <button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
